import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';

const VerifyEmailRequired: React.FC = () => {
  const location = useLocation();
  const initialEmail = (location.state as any)?.email || '';
  const [email, setEmail] = useState(initialEmail);
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setSending(true);
    setMessage(null);
    setError(null);
    try {
      const payload: { email: string; new_email?: string } = { email: email.trim() };
      if (newEmail.trim() && newEmail.trim().toLowerCase() !== email.trim().toLowerCase()) {
        payload.new_email = newEmail.trim();
      }
      const response = await authAPI.resendVerificationEmail(payload);
      if (response.email) {
        setEmail(response.email);
        setNewEmail('');
      }
      if (typeof response.remaining_attempts === 'number') {
        setRemainingAttempts(response.remaining_attempts);
        if (response.remaining_attempts === 0) {
          const retryAfter = response.retry_after_seconds || 24 * 3600;
          setCooldownUntil(Date.now() + retryAfter * 1000);
        }
      }
      setMessage(response.message || 'Verification email resent.');
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retry_after_seconds;
      if (retryAfter) {
        setCooldownUntil(Date.now() + retryAfter * 1000);
      }
      setError(err?.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setSending(false);
    }
  };

  const resendDisabled = sending || (cooldownUntil !== null && Date.now() < cooldownUntil);

  return (
    <div className="container center">
      <div className="card shadow" style={{ maxWidth: '420px', width: '100%' }}>
        <h2 className="h2">Verify your email to continue</h2>
        <p className="muted">
          {email
            ? `We sent a verification email to ${email}. Please click the button in that email to activate your account.`
            : 'We sent a verification email to your inbox. Please click the button in that email to activate your account.'}
        </p>
        {message && <div className="alert success">{message}</div>}
        {error && <div className="alert error">{error}</div>}
        {typeof remainingAttempts === 'number' && remainingAttempts >= 0 && (
          <div className="muted" style={{ marginTop: '8px' }}>
            Remaining resend attempts today: {remainingAttempts}
          </div>
        )}
        {remainingAttempts === 0 && (
          <div className="muted" style={{ marginTop: '4px' }}>
            Please wait before trying again.
          </div>
        )}
        <div className="form-group" style={{ marginTop: '16px' }}>
          <label className="label">Email address</label>
          <input
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
          />
        </div>
        <div className="form-group">
          <label className="label">New email (optional)</label>
          <input
            type="email"
            className="input"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter a corrected email"
          />
        </div>
        <button
          type="button"
          className="btn"
          onClick={handleResend}
          disabled={resendDisabled}
          style={{ marginTop: '8px' }}
        >
          {resendDisabled ? 'Resend disabled (limit reached)' : sending ? 'Sending...' : 'Resend verification email'}
        </button>
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <Link to="/login" className="btn">
            Back to login
          </Link>
          <Link to="/register" className="btn ghost">
            Use a different email
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailRequired;
